import { DataMap } from '@grapecity/wijmo.grid';
import { CellType } from '@grapecity/wijmo.grid';
import { CollectionView } from '@grapecity/wijmo';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';
import { useAuth } from 'context/AuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { getCurrentPeriod, getPeriods } from 'utils/dateUtils';
import { MessageModal } from 'components/modal/MessageModal';
import { ToastMessage } from 'components/modal/ToastMessage';
import SelectComponent from 'components/Input/SelectComponent';
import { toInt } from 'utils/numberUtils';
import { BusyoCd, BusyoCds } from 'constants/constants';

/** 目標設定画面 */
function GoalsSettingModal(props) {
  const auth = useAuth();
  const restApi = useRestApi();
  const master = useMaster();
  var planerMap = new DataMap(master.planer, 'id', 'name');
  var busyoMap = new DataMap(BusyoCds, 'key', 'value');

  const months = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];
  // 期
  const periods = getPeriods();

  // 選択中の期
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentPeriod());
  // 確認ダイアログ
  const [dialog, setDialog] = useState({});
  // トースト表示
  const [toast, setToast] = useState({});
  // 全体
  const [allList, setAllList] = useState(new CollectionView());
  // 営業担当別
  const [tantoList, setTantoList] = useState(new CollectionView());
  // FlexGrid選択行
  const [selectedTantoRow, setSelectedTantoRow] = useState(null);
  // 営業担当別グリッド参照
  const tantoGridRef = useRef(null);
  // 工事部グリッド参照
  const koujiGridRef = useRef(null);
  // 工事部
  const [otherList, setOtherList] = useState(new CollectionView());
  // 表示
  const [show, setShow] = useState(false);

  // 初期表示
  useEffect(() => {
    if (props.params?.show) {
      const period = getCurrentPeriod();
      setSelectedPeriod(period);
      setShow(true);
    }
  }, [props.params]);

  const calcTotal = (target) => {
    return Array.from({ length: 12 }, (_, i) => i + 1).reduce(
      (sum, i) => sum + toInt(target[`M${String(i).padStart(2, '0')}`], 0),
      0
    );
  };

  // 期の変更時の処理
  useEffect(() => {
    if (!selectedPeriod) return;
    restApi.get(`/api/analysis/weekly/goals/${selectedPeriod}`, (data) => {
      setAllList(
        new CollectionView([
          { ...data.soudan_count, DISPLAY: '相談数', SUBTOTAL: calcTotal(data.soudan_count) },
          { ...data.kei_count, DISPLAY: '契約数', SUBTOTAL: calcTotal(data.kei_count) },
          calcAmountTotal([...data.tanto_amount, data.other_amount]),
        ])
      );

      setTantoList(new CollectionView(data.tanto_amount));
      setOtherList(
        new CollectionView([
          { ...data.other_amount, DISPLAY: '工事部', SUBTOTAL: calcTotal(data.other_amount) },
        ])
      );
      setSelectedTantoRow(data.tanto_amount?.length > 0 ? 0 : null);
    });
  }, [selectedPeriod]);

  // 売り上げの合計を計算
  const calcAmountTotal = (items) => {
    let total = { DISPLAY: '売上' };
    for (let i = 1; i <= 12; i++) {
      total[`M${String(i).padStart(2, '0')}`] = items.reduce(
        (sum, item) => sum + toInt(item[`M${String(i).padStart(2, '0')}`], 0),
        0
      );
    }
    total.SUBTOTAL = calcTotal(total);
    return total;
  };

  // 全体リストを再描画(合計の再計算処理)
  const reflashAllList = () => {
    const targetTantoList = tantoGridRef.current?.rows.map((r) => r.dataItem) ?? tantoList.items;
    const targetKoujiList = koujiGridRef.current?.rows.map((r) => r.dataItem) ?? otherList.items;

    // 再計算を実施
    const new_all = allList.items.map((x) =>
      x.DISPLAY != '売上' ? x : calcAmountTotal([...targetTantoList, ...targetKoujiList])
    );

    allList.refresh();
    // 再描画（CVを再生成して確実に反映）
    setAllList(new CollectionView(new_all));
  };

  // 明細追加ボタン
  const handlenOnClickAdd = (busyoCd) => {
    // 部署ごとの末尾に挿入（営業部のみ0件時は先頭）
    const newItem = {
      PERIOD: selectedPeriod,
      GOAL_KIND: 2, // 2:社員目標
      VALUE_TYPE: 1, // 1:売り上げ
      BUSYO_CD: busyoCd,
    };

    const items = [...tantoList.items];
    const lastIdxInDept = items.reduce(
      (last, it, idx) => (it.BUSYO_CD === busyoCd ? idx : last),
      -1
    );

    let insertIdx;
    if (lastIdxInDept >= 0) {
      // 同一部署があればその末尾の次に入れる
      insertIdx = lastIdxInDept + 1;
    } else if (busyoCd === BusyoCd.SALES) {
      // 営業部で0件なら先頭
      insertIdx = 0;
    } else {
      // それ以外は末尾
      insertIdx = items.length;
    }

    items.splice(insertIdx, 0, newItem);
    // データ更新（CollectionView を作り直さずに更新）
    tantoList.sourceCollection = items;
    tantoList.refresh();
  };

  // 明細削除ボタン
  const handleOnClickDelete = (item) => (e) => {
    // 登録用の明細から削除
    tantoList.remove(item);
    reflashAllList();
  };

  // モーダルの閉じる処理
  const handleClose = () => {
    setSelectedPeriod(null);
    props.params.callBack();
    setShow(false);
  };

  // 保存ボタン押下
  const handleOnClickSave = () => {
    if (tantoList.items.some((x) => !x.GOAL_CD)) {
      setDialog({
        type: 'error',
        message: `担当を選択してください。`,
      });
      return;
    }

    // 担当の重複チェック
    const countMap = tantoList.items.reduce((acc, item) => {
      acc[item.GOAL_CD] = (acc[item.GOAL_CD] || 0) + 1;
      return acc;
    }, {});
    const duplicatedIds = Object.keys(countMap).filter((key) => countMap[key] > 1);
    if (duplicatedIds.length > 0) {
      setDialog({
        type: 'error',
        message: `担当が重複しています。(${duplicatedIds.map((id) =>
          planerMap.getDisplayValue(id)
        )})`,
      });
      return;
    }

    restApi.post(
      `/api/analysis/weekly/goals/save`,
      {
        soudan_count: allList.items[0], // 相談件数
        kei_count: allList.items[1], // 契約件数
        tanto_amount: tantoList.items, // 営業件数
        other_amount: otherList.items[0], // 工事部
      },
      (data) => {
        setToast({ type: 'info', message: '目標を保存しました。' });
      }
    );
  };

  // 編集開始処理
  const beginningEdit = (grid, args) => {
    var dataItem = grid.rows[args.row].dataItem;
    var col = grid.columns[args.col];
    // 全体の売り上げは編集不可
    if (dataItem?.DISPLAY === '売上') {
      args.cancel = true;
    }
    // 工事部の場合は担当コードのみ編集可能
    if (dataItem?.BUSYO_CD === BusyoCd.GENBA && col?.binding !== 'GOAL_CD') {
      args.cancel = true;
    }
  };

  // 編集終了処理
  const cellEditEnded = (grid, args) => {
    const tantoList = grid.rows.map((r) => r.dataItem);
    // 全体行を再描画
    reflashAllList();
  };

  // フォーマット処理
  const formatItem = (grid, args) => {
    if (args.panel.cellType === CellType.Cell) {
      var dataItem = grid.rows[args.row].dataItem;
      var col = grid.columns[args.col];

      args.cell.style.background = '';

      // 全体の売上げの場合は編集不可
      if (dataItem?.DISPLAY === '売上') {
        args.cell.style.background = 'rgb(236, 219, 219)';
      }

      // 工事部の場合は担当コードのみ編集可能
      if (dataItem?.BUSYO_CD === BusyoCd.GENBA && col?.binding !== 'GOAL_CD') {
        args.cell.style.background = 'rgb(236, 219, 219)';
      }

      if (col?.binding === 'SUBTOTAL') {
        const subtotal = Array.from({ length: 12 }, (_, i) => i + 1).reduce(
          (sum, i) => sum + toInt(dataItem[`M${String(i).padStart(2, '0')}`], 0),
          0
        );
        dataItem.SUBTOTAL = subtotal;
      }
    }
  };

  // 選択行の上下入れ替え
  const moveTantoRow = (rowIdx, direction) => {
    const items = [...tantoList.items];
    const targetIdx = rowIdx + direction;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    // 部署が異なる場合は入れ替え不可
    const src = items[rowIdx];
    const dst = items[targetIdx];
    if (!src || !dst || src.BUSYO_CD !== dst.BUSYO_CD) return;
    // swap
    [items[rowIdx], items[targetIdx]] = [items[targetIdx], items[rowIdx]];
    // データ更新（CollectionView を作り直さずに更新）
    tantoList.sourceCollection = items;
    tantoList.refresh();
    // 並べ替え後も同じ行を選択状態に保つ
    const grid = tantoGridRef.current;
    if (grid) {
      const col = grid.selection?.col ?? 0;
      grid.select(targetIdx, col);
    }
    reflashAllList();
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '1350px',
            },
          },
        }}
        open={show}
        disableEnforceFocus={true} // フォーカス制御を無効にする
      >
        <DialogTitle className="p-3">目標設定</DialogTitle>
        <DialogContent className="px-0 py-0 pr-0 pl-0">
          <Card className="m-0">
            <Card.Body className="p-3">
              <Row className="mb-2">
                {/* 期選択コンボボックス */}
                <Col className="pl-3" md="2">
                  <SelectComponent
                    value={selectedPeriod}
                    onChange={(e) => {
                      setSelectedPeriod(e.value);
                    }}
                  >
                    {periods.map((p) => (
                      <option key={p} value={p}>
                        {p}期
                      </option>
                    ))}
                  </SelectComponent>
                </Col>
              </Row>
              <Row>
                <Col style={{ fontWeight: 'bold' }}>全体</Col>
              </Row>
              <Row>
                <Col>
                  <FlexGrid
                    itemsSource={allList}
                    selectionMode={'Cell'}
                    headersVisibility={'Column'}
                    allowSorting={false}
                    formatItem={(s, e) => formatItem(s, e)}
                    beginningEdit={(s, e) => beginningEdit(s, e)}
                    pastingCell={(s, e) => beginningEdit(s, e)}
                  >
                    <FlexGridColumn
                      header={`${selectedPeriod}期`}
                      binding="DISPLAY"
                      isReadOnly={true}
                      width={165}
                      cssClass="cell-readonly"
                    ></FlexGridColumn>
                    {months.map((i) => (
                      <FlexGridColumn
                        header={`${i}月`}
                        binding={`M${String(i).padStart(2, '0')}`}
                        format="n0"
                        align="right"
                        dataType="Number"
                        width={84}
                      ></FlexGridColumn>
                    ))}
                    <FlexGridColumn
                      header="計"
                      binding="SUBTOTAL"
                      isReadOnly={true}
                      width={100}
                      dataType="Number"
                      cssClass="cell-readonly"
                    ></FlexGridColumn>
                  </FlexGrid>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col style={{ fontWeight: 'bold' }}>
                  担当別
                  <Button
                    className="material-symbols-rounded cell-icon-sm btn-fill ml-2"
                    variant="success"
                    title="上へ"
                    disabled={
                      selectedTantoRow === null ||
                      selectedTantoRow === 0 ||
                      tantoList.items[selectedTantoRow]?.BUSYO_CD !==
                        tantoList.items[selectedTantoRow - 1]?.BUSYO_CD
                    }
                    onClick={() => {
                      moveTantoRow(selectedTantoRow, -1);
                    }}
                  >
                    Arrow_Upward
                  </Button>
                  <Button
                    className="material-symbols-rounded cell-icon-sm btn-fill ml-1"
                    variant="success"
                    title="下へ"
                    disabled={
                      selectedTantoRow === null ||
                      selectedTantoRow >= tantoList.items.length - 1 ||
                      tantoList.items[selectedTantoRow]?.BUSYO_CD !==
                        tantoList.items[selectedTantoRow + 1]?.BUSYO_CD
                    }
                    onClick={() => {
                      moveTantoRow(selectedTantoRow, 1);
                    }}
                  >
                    Arrow_Downward
                  </Button>
                </Col>
                <Col className="text-right">
                  <Button
                    className="btn-fill goal-setting-button"
                    title="営業部を追加"
                    onClick={() => handlenOnClickAdd(BusyoCd.SALES)}
                  >
                    営業部を追加
                  </Button>
                  <Button
                    className="btn-fill goal-setting-button ml-1 mr-1"
                    title="工事部を追加"
                    onClick={() => handlenOnClickAdd(BusyoCd.GENBA)}
                  >
                    工事部を追加
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div
                    className="table-scroll"
                    style={{
                      height: '368px',
                    }}
                  >
                    <FlexGrid
                      itemsSource={tantoList}
                      selectionMode={'Cell'}
                      headersVisibility={'Column'}
                      allowSorting={false}
                      initialized={(s) => (tantoGridRef.current = s)}
                      formatItem={(s, e) => formatItem(s, e)}
                      beginningEdit={(s, e) => beginningEdit(s, e)}
                      cellEditEnded={(s, e) => cellEditEnded(s, e)}
                      pastingCell={(s, e) => beginningEdit(s, e)}
                      pasted={(s, e) => cellEditEnded(s, e)}
                      selectionChanged={(s, e) => {
                        setSelectedTantoRow(s.selection.row);
                      }}
                    >
                      <FlexGridColumn
                        header={`${selectedPeriod}期`}
                        binding="GOAL_CD"
                        dataMap={planerMap}
                        width={100}
                      ></FlexGridColumn>
                      <FlexGridColumn
                        header={`部署`}
                        binding="BUSYO_CD"
                        dataMap={busyoMap}
                        isReadOnly={true}
                        width={65}
                        cssClass="cell-readonly"
                      ></FlexGridColumn>
                      {months.map((i) => (
                        <FlexGridColumn
                          header={`${i}月`}
                          binding={`M${String(i).padStart(2, '0')}`}
                          align="right"
                          dataType="Number"
                          width={84}
                          isRequired={false}
                        ></FlexGridColumn>
                      ))}
                      <FlexGridColumn
                        header="計"
                        binding="SUBTOTAL"
                        isReadOnly={true}
                        width={100}
                        dataType="Number"
                        cssClass="cell-readonly"
                      ></FlexGridColumn>
                      <FlexGridColumn header="　" isReadOnly={true} width={28}>
                        <FlexGridCellTemplate
                          cellType="Cell"
                          template={(cell) => (
                            <Button
                              className="material-symbols-rounded cell-icon-sm btn-fill"
                              variant="danger"
                              title="削除"
                              onClick={handleOnClickDelete(cell.item)}
                            >
                              Clear
                            </Button>
                          )}
                        />
                      </FlexGridColumn>
                    </FlexGrid>
                  </div>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col style={{ fontWeight: 'bold' }}>工事部目標</Col>
              </Row>
              <Row>
                <Col>
                  <FlexGrid
                    initialized={(s) => (koujiGridRef.current = s)}
                    itemsSource={otherList}
                    selectionMode={'Cell'}
                    headersVisibility={'Column'}
                    allowSorting={false}
                    formatItem={(s, e) => formatItem(s, e)}
                    beginningEdit={(s, e) => beginningEdit(s, e)}
                    cellEditEnded={(s, e) => cellEditEnded(s, e)}
                    pastingCell={(s, e) => beginningEdit(s, e)}
                    pasted={(s, e) => cellEditEnded(s, e)}
                  >
                    <FlexGridColumn
                      header={`${selectedPeriod}期`}
                      binding="DISPLAY"
                      isReadOnly={true}
                      dataMap={planerMap}
                      width={165}
                    ></FlexGridColumn>
                    {months.map((i) => (
                      <FlexGridColumn
                        header={`${i}月`}
                        binding={`M${String(i).padStart(2, '0')}`}
                        format="n"
                        align="right"
                        dataType="Number"
                        isRequired={false}
                        width={84}
                      ></FlexGridColumn>
                    ))}
                    <FlexGridColumn
                      header="計"
                      binding="SUBTOTAL"
                      isReadOnly={true}
                      width={100}
                      dataType="Number"
                      cssClass="cell-readonly"
                    ></FlexGridColumn>
                  </FlexGrid>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </DialogContent>
        <DialogActions className="p-3" style={{ justifyContent: 'space-between' }}>
          <Button className="footer-button" variant="secondary" onClick={handleClose}>
            閉じる
          </Button>
          <Button
            className="btn-fill footer-button ml-3"
            variant="primary"
            onClick={handleOnClickSave}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* メッセージモーダル */}
      <MessageModal dialog={dialog}></MessageModal>
      {/* トーストメッセージ */}
      <ToastMessage toast={toast}></ToastMessage>
    </>
  );
}

export default GoalsSettingModal;
